import React, { ReactNode } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { getContrastText } from "@/utils/colorUtils";

interface GradientBackgroundProps {
  children: ReactNode;
  className?: string;
  intensity?: "light" | "medium" | "strong";
}

const GradientBackground: React.FC<GradientBackgroundProps> = ({
  children,
  className = "",
  intensity = "medium",
}) => {
  const { config } = useTenant();

  // Get tenant colors for styling
  const primaryColor = config?.branding?.colors?.primary || "#2563eb";
  const textColor = getContrastText(primaryColor);

  // Set opacity values based on intensity
  let opacityValues = {
    radialStart: "10",
    radialEnd: "20",
    linearStart: "99",
    linearMid1: "cc",
    linearMid2: "ee",
    linearEnd: "ff",
  };

  if (intensity === "light") {
    opacityValues = {
      radialStart: "08",
      radialEnd: "15",
      linearStart: "80",
      linearMid1: "99",
      linearMid2: "cc",
      linearEnd: "ee",
    };
  } else if (intensity === "strong") {
    opacityValues = {
      radialStart: "15",
      radialEnd: "30",
      linearStart: "aa",
      linearMid1: "cc",
      linearMid2: "ee",
      linearEnd: "ff",
    };
  }

  const backgroundStyle = {
    background: `
      radial-gradient(circle at 0% 0%, 
        ${primaryColor}${opacityValues.radialStart} 0%, 
        transparent 50%
      ),
      radial-gradient(circle at 100% 100%,
        ${primaryColor}${opacityValues.radialEnd} 0%,
        transparent 50%
      ),
      linear-gradient(
        135deg,
        ${primaryColor}${opacityValues.linearStart} 0%,
        ${primaryColor}${opacityValues.linearMid1} 40%,
        ${primaryColor}${opacityValues.linearMid2} 70%,
        ${primaryColor}${opacityValues.linearEnd} 100%
      )
    `,
    color: textColor,
  };

  return (
    <div className={className} style={backgroundStyle}>
      {children}
    </div>
  );
};

export default GradientBackground;
